/**
 * GoalAuthoringSection — the Studio's new "Goals" section (Goal Score
 * PR-7b, `docs/goal_score/F_ui_ux_designs.md` §2, mockup
 * `01_studio_goals_authoring.html`).
 *
 * A NEW top-level section, separate from "Checks" (the six existing
 * checkpoint types `RegionAnnotator` already authors) — not a seventh
 * panel type inside it. Mounted as a sibling Card in `ScoringStudio.tsx`,
 * reusing the SAME `annotations`/`onAnnotationsChange`/`phases`/
 * `templateId` the Focus Regions card already receives, since a Goal is
 * still just an `Annotation` with one of three additive config shapes —
 * never a separate data model.
 *
 * The opt-in IS the shape (no boolean flag anywhere):
 *   - Distance Goal: `movement_goal.travel_target` set.
 *   - Speed Goal:    `movement_goal.speed_target` set.
 *   - Angle Goal:    `(joint_angle_goal|axis_goal).aggregation === 'p95_max'`
 *                     WITH `angle_goal_target` set.
 *
 * Scope note, UPDATED by Goal Score follow-ups batch 1 (frontend), item C:
 * the point/joint picker here uses `FunctionalLandmarkPicker`, which supports
 * BOTH modes — Functional (chip grid, the default) and Anatomical ("click
 * the skeleton on the video"). Anatomical mode used to be disabled here
 * (`canClickSkeleton={false}`) because this section renders no video of its
 * own; `RegionAnnotator` (the sibling Card above this one in
 * `ScoringStudio.tsx`) now exposes an imperative handle
 * (`RegionAnnotatorHandle.requestLandmarkSelection`) that activates ITS OWN
 * `LandmarkPicker` overlay on ITS OWN video and reports the clicked landmark
 * back here — the exact same hit-testing the six Check panels use, with
 * zero duplication. `ScoringStudio.tsx` wires this through
 * `onRequestPickFromVideo`; a caller that omits the prop still gets the
 * pre-batch disabled-button behavior.
 */

import { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { Annotation } from './RegionAnnotator';
import FunctionalLandmarkPicker from './FunctionalLandmarkPicker';
import TravelDistanceEditor from './TravelDistanceEditor';
import FormEffectCurveEditor from './FormEffectCurveEditor';
import { getLandmarkLabel } from '@/utils/landmarkDetection';
import { previewGoal } from '@/services/goalAuthoringApi';
import type { Phase } from '@/types/api';
import type {
  GoalKind,
  SpeedTarget,
  AngleTarget,
  AngleGoalComparison,
  FormEffectCurve,
} from '@/types/goalAuthoring';
import { JOINT_NAMES } from './jointNames';
import {
  GOALS_SECTION_TITLE,
  GOALS_SECTION_SUBTITLE,
  GOALS_EMPTY_TITLE,
  GOALS_EMPTY_WHY,
  GOALS_EMPTY_HOW_BUTTON,
  GOAL_KIND_LABELS,
  GOAL_KIND_DESCRIPTIONS,
  GOAL_STEP_PICK_KIND_TITLE,
  GOAL_STEP_PICK_POINT_TITLE,
  GOAL_STEP_PICK_PHASE_TITLE,
  GOAL_STEP_PICK_PHASE_WHY,
  GOAL_STEP_PICK_PHASE_HOW,
  GOAL_STEP_WORTH_TITLE,
  GOAL_STEP_WORTH_WHY,
  GOAL_VALIDATION_NEEDS_KIND,
  GOAL_VALIDATION_NEEDS_POINT,
  GOAL_VALIDATION_NEEDS_PHASE,
  GOAL_VALIDATION_NEEDS_TARGET,
  GOAL_SOURCE_DEMO_LABEL,
  GOAL_SOURCE_TYPED_LABEL,
  GOAL_SPEED_DEMO_CAUTION_TITLE,
  GOAL_SPEED_DEMO_CAUTION_WHY,
  GOAL_ANGLE_FORK_TITLE,
  GOAL_ANGLE_FORK_WHY,
  GOAL_ANGLE_FORK_HOW,
  GOAL_ANGLE_FORK_OPTION_TARGET,
  GOAL_ANGLE_FORK_OPTION_LIMIT,
} from '@/utils/goalAuthoringCopy';

// ---------------------------------------------------------------------------
// Carrier detection — mirrors the backend's own eligibility predicates
// (goal_scoring.py's _travel_target_config/_speed_target_config/
// _angle_target_config) so the frontend never invents a second rule for
// "is this annotation a Goal".
// ---------------------------------------------------------------------------

function goalKindOf(ann: Annotation): GoalKind | null {
  if (ann.movement_goal?.travel_target) return 'distance';
  if (ann.movement_goal?.speed_target) return 'speed';
  const jointCarrier = ann.joint_angle_goal as unknown as { aggregation?: string; angle_goal_target?: unknown } | undefined;
  const axisCarrier = ann.axis_goal as unknown as { aggregation?: string; angle_goal_target?: unknown } | undefined;
  if (jointCarrier?.aggregation === 'p95_max' && jointCarrier.angle_goal_target) return 'angle';
  if (axisCarrier?.aggregation === 'p95_max' && axisCarrier.angle_goal_target) return 'angle';
  return null;
}

interface GoalRow {
  annotation: Annotation;
  index: number;
  kind: GoalKind;
}

function collectGoalRows(annotations: Annotation[]): GoalRow[] {
  const rows: GoalRow[] = [];
  annotations.forEach((ann, index) => {
    const kind = goalKindOf(ann);
    if (kind) rows.push({ annotation: ann, index, kind });
  });
  return rows;
}

// ---------------------------------------------------------------------------
// Draft shape the editor form works on before it is committed back into
// the annotations array as a real Annotation.
// ---------------------------------------------------------------------------

type ImportanceLevel = 'low' | 'medium' | 'high' | 'critical';

interface GoalDraft {
  id?: string;
  kind: GoalKind | null;
  point: string; // functional or anatomical landmark/joint name
  axisEndPoint: string; // Angle, axis_goal carrier only
  carrier: 'joint_angle_goal' | 'axis_goal'; // Angle only — which carrier
  targetPhase: string | null;
  importance: ImportanceLevel;
  travelTarget: { mode: 'demo' | 'typed'; value: number | null };
  speedTarget: { mode: 'demo' | 'typed'; value: number | null };
  angleTarget: { mode: 'demo' | 'typed'; value: number | null };
  angleComparison: AngleGoalComparison;
  angleIsSafetyLimit: boolean; // the depth-vs-safety fork, Angle only
}

function emptyDraft(): GoalDraft {
  return {
    kind: null,
    point: '',
    axisEndPoint: '',
    carrier: 'joint_angle_goal',
    targetPhase: null,
    importance: 'medium',
    travelTarget: { mode: 'demo', value: null },
    speedTarget: { mode: 'demo', value: null },
    angleTarget: { mode: 'demo', value: null },
    angleComparison: 'at_least',
    angleIsSafetyLimit: false,
  };
}

function draftFromAnnotation(ann: Annotation, kind: GoalKind): GoalDraft {
  const draft = emptyDraft();
  draft.id = ann.id;
  draft.kind = kind;
  draft.targetPhase = ann.target_phase ?? null;
  draft.importance = (ann.importance as ImportanceLevel) ?? 'medium';
  if (kind === 'distance') {
    const t = ann.movement_goal?.travel_target;
    draft.point = ann.movement_goal?.tracked_landmark ?? '';
    draft.travelTarget = {
      mode: t?.target_source === 'coach' ? 'typed' : 'demo',
      value: t?.value_body_heights ?? null,
    };
  } else if (kind === 'speed') {
    const t = ann.movement_goal?.speed_target as SpeedTarget | undefined;
    draft.point = ann.movement_goal?.tracked_landmark ?? '';
    draft.speedTarget = {
      mode: t?.target_source === 'coach' ? 'typed' : 'demo',
      value: t?.value_body_heights_per_s ?? null,
    };
  } else {
    const jointCarrier = ann.joint_angle_goal as unknown as { aggregation?: string; angle_goal_target?: AngleTarget; angle_goal_comparison?: AngleGoalComparison; joint_name?: string } | undefined;
    const axisCarrier = ann.axis_goal as unknown as { aggregation?: string; angle_goal_target?: AngleTarget; angle_goal_comparison?: AngleGoalComparison; landmark_start?: string; landmark_end?: string } | undefined;
    if (jointCarrier?.aggregation === 'p95_max') {
      draft.carrier = 'joint_angle_goal';
      draft.point = jointCarrier.joint_name ?? '';
      const t = jointCarrier.angle_goal_target;
      draft.angleTarget = { mode: t?.target_source === 'coach' ? 'typed' : 'demo', value: t?.value_degrees ?? null };
      draft.angleComparison = jointCarrier.angle_goal_comparison ?? 'at_least';
    } else if (axisCarrier?.aggregation === 'p95_max') {
      draft.carrier = 'axis_goal';
      draft.point = axisCarrier.landmark_start ?? '';
      draft.axisEndPoint = axisCarrier.landmark_end ?? '';
      const t = axisCarrier.angle_goal_target;
      draft.angleTarget = { mode: t?.target_source === 'coach' ? 'typed' : 'demo', value: t?.value_degrees ?? null };
      draft.angleComparison = axisCarrier.angle_goal_comparison ?? 'at_least';
    }
    draft.angleIsSafetyLimit = false; // a saved Angle Goal was, by construction, never the safety reading
  }
  return draft;
}

/** Commits a draft into a real `Annotation` — additive to whatever the
 * annotation already carried (an existing Check being "turned into a
 * Goal" keeps its other fields; see I §7's "Turn this into a Goal" copy). */
function draftToAnnotation(draft: GoalDraft, existing: Annotation | null): Annotation {
  const base: Annotation = existing
    ? { ...existing }
    : {
        id: `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'line',
        importance: draft.importance,
        description: GOAL_KIND_LABELS[draft.kind ?? 'distance'],
        color: '#6366F1',
        tracking: false,
      };
  base.importance = draft.importance;
  base.target_phase = draft.targetPhase;

  if (draft.kind === 'distance') {
    base.movement_goal = {
      ...(base.movement_goal ?? { enabled: true, mode: 'direction_only' as const, reference_type: 'phase_start' as const, aggregation: 'max_displacement' as const, comparison: 'at_least' as const }),
      tracked_landmark: draft.point,
      speed_target: undefined,
      travel_target: {
        target_source: draft.travelTarget.mode === 'typed' ? 'coach' : 'demo',
        value_body_heights: draft.travelTarget.mode === 'typed' ? draft.travelTarget.value : null,
        comparison: 'at_least',
      },
    };
    base.joint_angle_goal = undefined;
    base.axis_goal = undefined;
  } else if (draft.kind === 'speed') {
    base.movement_goal = {
      ...(base.movement_goal ?? { enabled: true, mode: 'direction_only' as const, reference_type: 'phase_start' as const, aggregation: 'max_displacement' as const, comparison: 'at_least' as const }),
      tracked_landmark: draft.point,
      travel_target: undefined,
      speed_target: {
        target_source: draft.speedTarget.mode === 'typed' ? 'coach' : 'demo',
        value_body_heights_per_s: draft.speedTarget.mode === 'typed' ? draft.speedTarget.value : null,
        comparison: 'at_least',
      } as SpeedTarget,
    };
    base.joint_angle_goal = undefined;
    base.axis_goal = undefined;
  } else if (draft.kind === 'angle') {
    const angleTarget: AngleTarget = {
      target_source: draft.angleTarget.mode === 'typed' ? 'coach' : 'demo',
      value_degrees: draft.angleTarget.mode === 'typed' ? draft.angleTarget.value : null,
    };
    // The depth-vs-safety fork (REPORT §3.3/§6.1): a coach's "Limit" choice
    // under at_most is NEVER authored as a Goal — it routes to a plain
    // Angle Check with is_gate instead, so this component must not emit
    // aggregation: 'p95_max' for that case. The caller-facing flow (below)
    // refuses to save in that state; this function only ever receives a
    // draft that has already cleared that gate.
    if (draft.carrier === 'joint_angle_goal') {
      base.joint_angle_goal = {
        ...(base.joint_angle_goal ?? { enabled: true, reference_type: 'target_range', angle_tolerance_degrees: 15 }),
        joint_name: draft.point,
        aggregation: 'p95_max',
        angle_goal_comparison: draft.angleComparison,
        angle_goal_target: angleTarget,
      } as Annotation['joint_angle_goal'];
      base.axis_goal = undefined;
    } else {
      base.axis_goal = {
        ...(base.axis_goal ?? { enabled: true, landmark_start: draft.point, landmark_end: draft.axisEndPoint, reference_type: 'vertical', target_angle_degrees: 90, angle_tolerance_degrees: 15, measure_stability: false }),
        landmark_start: draft.point,
        landmark_end: draft.axisEndPoint,
        aggregation: 'p95_max',
        angle_goal_comparison: draft.angleComparison,
        angle_goal_target: angleTarget,
      } as Annotation['axis_goal'];
      base.joint_angle_goal = undefined;
    }
    base.movement_goal = undefined;
  }
  return base;
}

function draftValidationError(draft: GoalDraft): string | null {
  if (!draft.kind) return GOAL_VALIDATION_NEEDS_KIND;
  if (!draft.point) return GOAL_VALIDATION_NEEDS_POINT;
  if (draft.kind === 'angle' && draft.carrier === 'axis_goal' && !draft.axisEndPoint) return GOAL_VALIDATION_NEEDS_POINT;
  if (!draft.targetPhase) return GOAL_VALIDATION_NEEDS_PHASE;
  if (draft.kind === 'angle' && draft.angleIsSafetyLimit) {
    return 'A safety limit should be set up as a Check with a hard limit, not a Goal — pick "Target to beat" instead, or cancel and add it as a Check.';
  }
  const t =
    draft.kind === 'distance' ? draft.travelTarget : draft.kind === 'speed' ? draft.speedTarget : draft.angleTarget;
  if (t.mode === 'typed' && (t.value === null || !Number.isFinite(t.value))) return GOAL_VALIDATION_NEEDS_TARGET;
  return null;
}

// ---------------------------------------------------------------------------
// Main section
// ---------------------------------------------------------------------------

export interface GoalAuthoringSectionProps {
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  phases: Phase[];
  templateId?: string;
  formEffectCurve?: FormEffectCurve;
  onFormEffectCurveChange?: (curve: FormEffectCurve) => void;
  className?: string;
  /**
   * Goal Score follow-ups batch 1 (frontend), item C: resolves the scope
   * note at the top of this file — `RegionAnnotator` (the sibling Card
   * above this one in `ScoringStudio.tsx`) now exposes its own video's
   * landmark-picker overlay via an imperative handle. When the parent wires
   * this prop (it always does, in `ScoringStudio.tsx`), `FunctionalLandmarkPicker`'s
   * existing Anatomical-mode "click skeleton" button is enabled here too —
   * no second hit-testing implementation; this just forwards to the SAME
   * `LandmarkPicker` overlay the six Check panels already use. Optional so a
   * caller that genuinely has no video on screen (e.g. a future standalone
   * use of this section) still renders correctly with the button disabled,
   * exactly as before this batch.
   */
  onRequestPickFromVideo?: (onSelect: (landmark: string) => void) => void;
  /** Disarm a pending skeleton pick (called on cancel, save and unmount). */
  onCancelPickFromVideo?: () => void;
}

export default function GoalAuthoringSection({
  annotations,
  onAnnotationsChange,
  phases,
  templateId,
  formEffectCurve,
  onFormEffectCurveChange,
  className,
  onRequestPickFromVideo,
  onCancelPickFromVideo,
}: GoalAuthoringSectionProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<GoalDraft | null>(null);

  // A skeleton pick requested from a draft must not outlive that draft
  // (red-team finding 1): disarm it whenever the section unmounts.
  useEffect(() => () => onCancelPickFromVideo?.(), [onCancelPickFromVideo]);

  const goalRows = collectGoalRows(annotations);

  const startAdd = () => {
    setEditingIndex(null);
    setDraft(emptyDraft());
  };

  const startEdit = (row: GoalRow) => {
    setEditingIndex(row.index);
    setDraft(draftFromAnnotation(row.annotation, row.kind));
  };

  const cancel = () => {
    onCancelPickFromVideo?.();
    setEditingIndex(null);
    setDraft(null);
  };

  const remove = (index: number) => {
    onAnnotationsChange(annotations.filter((_, i) => i !== index));
  };

  const save = () => {
    if (!draft) return;
    if (draftValidationError(draft)) return;
    const existing = editingIndex !== null ? annotations[editingIndex] : null;
    const next = draftToAnnotation(draft, existing);
    if (editingIndex !== null) {
      onAnnotationsChange(annotations.map((a, i) => (i === editingIndex ? next : a)));
    } else {
      onAnnotationsChange([...annotations, next]);
    }
    cancel();
  };

  return (
    <div className={cn('space-y-3', className)} data-testid="goal-authoring-section">
      <div>
        <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-accent-indigo" />
          {GOALS_SECTION_TITLE}
        </h3>
        <p className="text-sm text-text-muted mt-0.5">{GOALS_SECTION_SUBTITLE}</p>
      </div>

      {goalRows.length > 0 && onFormEffectCurveChange && (
        <FormEffectCurveEditor
          value={formEffectCurve}
          onChange={onFormEffectCurveChange}
          templateId={templateId}
        />
      )}

      {goalRows.length === 0 && !draft && (
        <div
          className="rounded-lg border border-dashed border-border-subtle bg-dark-elevated-1 p-4 text-center space-y-2"
          data-testid="goals-empty-state"
        >
          <p className="text-sm font-medium text-text-primary">{GOALS_EMPTY_TITLE}</p>
          <p className="text-xs text-text-muted">{GOALS_EMPTY_WHY}</p>
          <button
            type="button"
            onClick={startAdd}
            className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-md bg-accent-indigo text-white text-sm font-medium hover:bg-accent-indigo/90 transition-colors"
            data-testid="add-goal-button"
          >
            <PlusIcon className="h-4 w-4" />
            {GOALS_EMPTY_HOW_BUTTON}
          </button>
        </div>
      )}

      {goalRows.length > 0 && (
        <div className="space-y-1.5" data-testid="goal-list">
          {goalRows.map((row) => (
            <div
              key={row.annotation.id}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border-subtle bg-dark-elevated-1"
              data-testid={`goal-row-${row.annotation.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary truncate">
                  {GOAL_KIND_LABELS[row.kind]}: {row.annotation.description || GOAL_KIND_DESCRIPTIONS[row.kind]}
                </p>
                {row.annotation.target_phase && (
                  <p className="text-xs text-text-muted">Phase: {row.annotation.target_phase}</p>
                )}
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => startEdit(row)}
                  className="p-1 rounded text-text-muted hover:text-accent-indigo hover:bg-dark-elevated-2"
                  aria-label="Edit Goal"
                  data-testid={`edit-goal-${row.annotation.id}`}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(row.index)}
                  className="p-1 rounded text-text-muted hover:text-danger hover:bg-dark-elevated-2"
                  aria-label="Remove Goal"
                  data-testid={`remove-goal-${row.annotation.id}`}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
          {!draft && (
            <button
              type="button"
              onClick={startAdd}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-accent-indigo/40 text-accent-indigo text-sm font-medium hover:bg-accent-indigo/10 transition-colors"
              data-testid="add-goal-button"
            >
              <PlusIcon className="h-4 w-4" />
              {GOALS_EMPTY_HOW_BUTTON}
            </button>
          )}
        </div>
      )}

      {draft && (
        <GoalEditorForm
          draft={draft}
          onChange={setDraft}
          onSave={save}
          onCancel={cancel}
          phases={phases}
          templateId={templateId}
          onRequestPickFromVideo={onRequestPickFromVideo}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The editor form — kind -> point -> phase -> target -> importance.
// ---------------------------------------------------------------------------

const KIND_ORDER: GoalKind[] = ['distance', 'speed', 'angle'];
const IMPORTANCE_LEVELS: ImportanceLevel[] = ['low', 'medium', 'high', 'critical'];

function GoalEditorForm({
  draft,
  onChange,
  onSave,
  onCancel,
  phases,
  templateId,
  onRequestPickFromVideo,
}: {
  draft: GoalDraft;
  onChange: (d: GoalDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  phases: Phase[];
  templateId?: string;
  onRequestPickFromVideo?: (onSelect: (landmark: string) => void) => void;
}) {
  const error = draftValidationError(draft);

  return (
    <div
      className="space-y-3 rounded-lg border border-accent-indigo/40 bg-dark-elevated-1 p-3"
      data-testid="goal-editor-form"
    >
      {/* Step 1 — kind */}
      <div>
        <p className="text-sm font-medium text-text-primary mb-1.5">{GOAL_STEP_PICK_KIND_TITLE}</p>
        <div className="flex gap-2">
          {KIND_ORDER.map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => onChange({ ...draft, kind })}
              className={cn(
                'flex-1 rounded-lg border-2 p-2 text-left transition-colors',
                draft.kind === kind
                  ? 'border-accent-indigo bg-accent-indigo/10'
                  : 'border-border-subtle bg-dark-card hover:border-accent-indigo/40',
              )}
              data-testid={`goal-kind-card-${kind}`}
            >
              <p className="text-sm font-semibold text-text-primary">{GOAL_KIND_LABELS[kind]}</p>
              <p className="text-[11px] text-text-muted">{GOAL_KIND_DESCRIPTIONS[kind]}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2 — point(s) */}
      {draft.kind && (
        <div>
          <p className="text-sm font-medium text-text-primary mb-1.5">{GOAL_STEP_PICK_POINT_TITLE}</p>
          {draft.kind === 'angle' && (
            <div className="flex gap-1 mb-2" role="radiogroup" aria-label="Angle carrier">
              <button
                type="button"
                onClick={() => onChange({ ...draft, carrier: 'joint_angle_goal' })}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium',
                  draft.carrier === 'joint_angle_goal' ? 'bg-accent-indigo text-white' : 'bg-dark-card text-text-secondary',
                )}
                data-testid="goal-angle-carrier-joint"
              >
                A joint
              </button>
              <button
                type="button"
                onClick={() => onChange({ ...draft, carrier: 'axis_goal' })}
                className={cn(
                  'px-2 py-1 rounded text-xs font-medium',
                  draft.carrier === 'axis_goal' ? 'bg-accent-indigo text-white' : 'bg-dark-card text-text-secondary',
                )}
                data-testid="goal-angle-carrier-axis"
              >
                Two points
              </button>
            </div>
          )}
          {draft.kind === 'angle' && draft.carrier === 'joint_angle_goal' ? (
            <select
              value={draft.point}
              onChange={(e) => onChange({ ...draft, point: e.target.value })}
              className="w-full px-2 py-1.5 bg-dark-card text-text-primary text-sm rounded border border-border-subtle"
              data-testid="goal-joint-select"
            >
              <option value="">Select a joint…</option>
              {JOINT_NAMES.map((j) => (
                <option key={j.value} value={j.value}>
                  {j.label}
                </option>
              ))}
            </select>
          ) : (
            <FunctionalLandmarkPicker
              value={draft.point}
              onChange={(landmark) => onChange({ ...draft, point: landmark })}
              onClickSkeleton={() =>
                onRequestPickFromVideo?.((landmark) => onChange({ ...draft, point: landmark }))
              }
              canClickSkeleton={Boolean(onRequestPickFromVideo)}
              side="A"
            />
          )}
          {draft.kind === 'angle' && draft.carrier === 'axis_goal' && (
            <div className="mt-2">
              <p className="text-xs text-text-secondary mb-1">And the second point:</p>
              <FunctionalLandmarkPicker
                value={draft.axisEndPoint}
                onChange={(landmark) => onChange({ ...draft, axisEndPoint: landmark })}
                onClickSkeleton={() =>
                  onRequestPickFromVideo?.((landmark) => onChange({ ...draft, axisEndPoint: landmark }))
                }
                canClickSkeleton={Boolean(onRequestPickFromVideo)}
                side="B"
              />
            </div>
          )}
        </div>
      )}

      {/* Step 3 — phase (required) */}
      {draft.kind && draft.point && (
        <div>
          <p className="text-sm font-medium text-text-primary mb-1">{GOAL_STEP_PICK_PHASE_TITLE}</p>
          <p className="text-[11px] text-text-muted mb-1">{GOAL_STEP_PICK_PHASE_WHY}</p>
          <select
            value={draft.targetPhase ?? ''}
            onChange={(e) => onChange({ ...draft, targetPhase: e.target.value || null })}
            className="w-full px-2 py-1.5 bg-dark-card text-text-primary text-sm rounded border border-border-subtle"
            data-testid="goal-phase-select"
          >
            <option value="">Select a phase…</option>
            {phases.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-text-muted mt-1">{GOAL_STEP_PICK_PHASE_HOW}</p>
        </div>
      )}

      {/* Step 4 — target */}
      {draft.kind === 'distance' && draft.targetPhase && (
        <TravelDistanceEditor
          value={{
            target_source: draft.travelTarget.mode === 'typed' ? 'coach' : 'demo',
            value_body_heights: draft.travelTarget.value ?? undefined,
          }}
          onChange={(v) =>
            onChange({
              ...draft,
              travelTarget: { mode: v.target_source === 'coach' ? 'typed' : 'demo', value: v.value_body_heights ?? null },
            })
          }
          templateId={templateId}
          landmark={draft.point}
          landmarkLabel={getLandmarkLabel(draft.point)}
          targetPhase={draft.targetPhase}
        />
      )}

      {draft.kind === 'speed' && draft.targetPhase && (
        <SpeedTargetFields
          draft={draft}
          onChange={onChange}
          templateId={templateId}
        />
      )}

      {draft.kind === 'angle' && draft.targetPhase && (draft.carrier === 'joint_angle_goal' ? draft.point : draft.axisEndPoint) && (
        <AngleTargetFields draft={draft} onChange={onChange} templateId={templateId} />
      )}

      {/* Step 5 — importance (reuses the existing Low/Medium/High/Critical
          scale Checks already use — the Goal-score bonus's own importance
          weight comes from the SAME get_importance_multipliers() table,
          not a separate points stepper; see goal_scoring._project_goal). */}
      {draft.kind && draft.targetPhase && (
        <div>
          <p className="text-sm font-medium text-text-primary mb-1">{GOAL_STEP_WORTH_TITLE}</p>
          <p className="text-[11px] text-text-muted mb-1.5">{GOAL_STEP_WORTH_WHY}</p>
          <div className="flex gap-1.5">
            {IMPORTANCE_LEVELS.map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => onChange({ ...draft, importance: level })}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium capitalize border',
                  draft.importance === level
                    ? 'bg-accent-indigo text-white border-accent-indigo'
                    : 'bg-dark-card text-text-secondary border-border-subtle',
                )}
                data-testid={`goal-importance-${level}`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-danger" data-testid="goal-editor-error">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onSave}
          disabled={!!error}
          className="px-3 py-1.5 rounded-md bg-accent-indigo text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="goal-save-button"
        >
          Save Goal
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-text-secondary text-sm font-medium hover:bg-dark-elevated-2"
          data-testid="goal-cancel-button"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Speed target fields — the A/B source bar (demo vs typed), with the
// Speed-specific "your demo might be slow" caution B §5.5/REPORT §3.2
// requires (a product requirement, not a copy nit).
// ---------------------------------------------------------------------------

function SpeedTargetFields({
  draft,
  onChange,
  templateId,
}: {
  draft: GoalDraft;
  onChange: (d: GoalDraft) => void;
  templateId?: string;
}) {
  const [demoValue, setDemoValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleMeasureDemo = () => {
    if (!templateId || !draft.point) return;
    setLoading(true);
    previewGoal(templateId, { kind: 'speed', landmark: draft.point, target_phase: draft.targetPhase })
      .then((data) => {
        setLoading(false);
        if (data.status === 'computed') setDemoValue(data.speed_body_heights_per_s);
      })
      .catch(() => setLoading(false));
  };

  return (
    <div className="space-y-2 rounded-lg border border-border-subtle bg-dark-card p-3" data-testid="speed-target-fields">
      <div className="flex items-start gap-2 p-2 bg-warning/10 border border-warning/30 rounded text-xs text-warning">
        <span>
          <strong>{GOAL_SPEED_DEMO_CAUTION_TITLE}</strong> {GOAL_SPEED_DEMO_CAUTION_WHY}
        </span>
      </div>
      <div className="space-y-1.5" role="radiogroup" aria-label="How to set the speed target">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={draft.speedTarget.mode === 'demo'}
            onChange={() => onChange({ ...draft, speedTarget: { mode: 'demo', value: null } })}
            data-testid="speed-source-demo-radio"
          />
          <span className="text-sm text-text-primary">{GOAL_SOURCE_DEMO_LABEL}</span>
          {draft.speedTarget.mode === 'demo' && (
            <button type="button" onClick={handleMeasureDemo} className="text-xs text-accent-indigo underline" disabled={loading}>
              {loading ? 'Measuring…' : 'Measure'}
            </button>
          )}
          {draft.speedTarget.mode === 'demo' && demoValue !== null && (
            <span className="text-xs text-text-muted">Your demo: {demoValue.toFixed(2)} body heights/s</span>
          )}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            checked={draft.speedTarget.mode === 'typed'}
            onChange={() => onChange({ ...draft, speedTarget: { mode: 'typed', value: draft.speedTarget.value } })}
            data-testid="speed-source-typed-radio"
          />
          <span className="text-sm text-text-primary">{GOAL_SOURCE_TYPED_LABEL}</span>
          {draft.speedTarget.mode === 'typed' && (
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={draft.speedTarget.value ?? ''}
              onChange={(e) =>
                onChange({ ...draft, speedTarget: { mode: 'typed', value: e.target.value === '' ? null : Number(e.target.value) } })
              }
              className="w-20 px-2 py-1 bg-dark-elevated-1 text-text-primary text-sm text-center rounded border border-border-subtle"
              data-testid="speed-typed-value-input"
            />
          )}
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Angle target fields — target source A/B + comparison + the depth-vs-
// safety-ceiling fork (I §7 / REPORT §3.3/§6.1, verbatim copy).
// ---------------------------------------------------------------------------

function AngleTargetFields({
  draft,
  onChange,
  templateId,
}: {
  draft: GoalDraft;
  onChange: (d: GoalDraft) => void;
  templateId?: string;
}) {
  const [demoValue, setDemoValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleMeasureDemo = () => {
    if (!templateId) return;
    setLoading(true);
    const body =
      draft.carrier === 'joint_angle_goal'
        ? { kind: 'angle' as const, joint_name: draft.point, target_phase: draft.targetPhase }
        : { kind: 'angle' as const, landmark_start: draft.point, landmark_end: draft.axisEndPoint, target_phase: draft.targetPhase };
    previewGoal(templateId, body)
      .then((data) => {
        setLoading(false);
        if (data.status === 'computed') setDemoValue(data.p95_degrees);
      })
      .catch(() => setLoading(false));
  };

  return (
    <div className="space-y-3 rounded-lg border border-border-subtle bg-dark-card p-3" data-testid="angle-target-fields">
      <div>
        <p className="text-sm font-medium text-text-primary">How should we compare?</p>
        <div className="flex gap-2 mt-1">
          <button
            type="button"
            onClick={() => onChange({ ...draft, angleComparison: 'at_least', angleIsSafetyLimit: false })}
            className={cn(
              'px-2.5 py-1 rounded-md text-xs font-medium border',
              draft.angleComparison === 'at_least' ? 'bg-accent-indigo text-white border-accent-indigo' : 'bg-dark-elevated-1 text-text-secondary border-border-subtle',
            )}
            data-testid="angle-comparison-at-least"
          >
            At least this bent
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...draft, angleComparison: 'at_most' })}
            className={cn(
              'px-2.5 py-1 rounded-md text-xs font-medium border',
              draft.angleComparison === 'at_most' ? 'bg-accent-indigo text-white border-accent-indigo' : 'bg-dark-elevated-1 text-text-secondary border-border-subtle',
            )}
            data-testid="angle-comparison-at-most"
          >
            At most this bent
          </button>
        </div>
      </div>

      {/* The depth-vs-safety-ceiling fork — shown the moment "at most" is picked. */}
      {draft.angleComparison === 'at_most' && (
        <div className="space-y-1.5 p-2 rounded-lg border border-info/30 bg-info/5">
          <p className="text-sm font-medium text-text-primary">{GOAL_ANGLE_FORK_TITLE}</p>
          <p className="text-[11px] text-text-secondary">{GOAL_ANGLE_FORK_WHY}</p>
          <p className="text-[11px] text-text-muted">{GOAL_ANGLE_FORK_HOW}</p>
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              onClick={() => onChange({ ...draft, angleIsSafetyLimit: false })}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border',
                !draft.angleIsSafetyLimit ? 'bg-success text-white border-success' : 'bg-dark-elevated-1 text-text-secondary border-border-subtle',
              )}
              data-testid="angle-fork-target"
            >
              {GOAL_ANGLE_FORK_OPTION_TARGET}
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...draft, angleIsSafetyLimit: true })}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border',
                draft.angleIsSafetyLimit ? 'bg-warning text-white border-warning' : 'bg-dark-elevated-1 text-text-secondary border-border-subtle',
              )}
              data-testid="angle-fork-limit"
            >
              {GOAL_ANGLE_FORK_OPTION_LIMIT}
            </button>
          </div>
          {draft.angleIsSafetyLimit && (
            <p className="text-[11px] text-warning" data-testid="angle-fork-limit-notice">
              This will not be saved as a Goal — remove it here and add it under Checks as a hard limit instead.
            </p>
          )}
        </div>
      )}

      {!draft.angleIsSafetyLimit && (
        <div className="space-y-1.5" role="radiogroup" aria-label="How to set the angle target">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={draft.angleTarget.mode === 'demo'}
              onChange={() => onChange({ ...draft, angleTarget: { mode: 'demo', value: null } })}
              data-testid="angle-source-demo-radio"
            />
            <span className="text-sm text-text-primary">{GOAL_SOURCE_DEMO_LABEL}</span>
            {draft.angleTarget.mode === 'demo' && (
              <button type="button" onClick={handleMeasureDemo} className="text-xs text-accent-indigo underline" disabled={loading}>
                {loading ? 'Measuring…' : 'Measure'}
              </button>
            )}
            {draft.angleTarget.mode === 'demo' && demoValue !== null && (
              <span className="text-xs text-text-muted">Your demo: {demoValue.toFixed(1)}°</span>
            )}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={draft.angleTarget.mode === 'typed'}
              onChange={() => onChange({ ...draft, angleTarget: { mode: 'typed', value: draft.angleTarget.value } })}
              data-testid="angle-source-typed-radio"
            />
            <span className="text-sm text-text-primary">{GOAL_SOURCE_TYPED_LABEL}</span>
            {draft.angleTarget.mode === 'typed' && (
              <input
                type="number"
                step={0.1}
                value={draft.angleTarget.value ?? ''}
                onChange={(e) =>
                  onChange({ ...draft, angleTarget: { mode: 'typed', value: e.target.value === '' ? null : Number(e.target.value) } })
                }
                className="w-20 px-2 py-1 bg-dark-elevated-1 text-text-primary text-sm text-center rounded border border-border-subtle"
                data-testid="angle-typed-value-input"
              />
            )}
          </label>
        </div>
      )}
    </div>
  );
}
